function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useContext, useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import cn from 'classnames';
import useStyles from 'isomorphic-style-loader/useStyles';
import moment from 'moment';
import { formatDate } from 'helpers/datetime';
import { useAction, useConversion, useStandards } from 'hooks';
import { MedicalRecordContext, ProfileContext } from 'context';
import CloudDrive from 'modules/CloudDrive';
import messages from '../../messages';
import { GlucoseConcentrationTrend } from '../../modules/Charts';
import GlucoseLevelDistributionChart from './GlucoseLevelDistributionBarChart';
import { useChartData, useTicks } from './hooks';
import Header from './Header';
import Table from './Table';
import styles from './BloodGlucoseConcentrationReport.pcss';
const gridTheme = {
    grid: {
        stroke: '#E0E8F4',
        strokeWidth: 1
    }
};
const formatLabel = ()=>'';
const BloodGlucoseConcentrationReport = ({ customRanges })=>{
    useStyles(styles);
    const intl = useIntl();
    const profileContext = useContext(ProfileContext);
    const medicalRecord = useContext(MedicalRecordContext);
    const phiSet = useSelector(profileContext.selectors.phiSet);
    const phiSetDocumentId = useSelector(profileContext.selectors.phiSetDocumentId);
    const batchesIndex = useSelector(profileContext.selectors.batchesIndex);
    const measurementsBatchesIndex = useSelector(profileContext.selectors.measurementsBatchesIndex);
    const readings30 = useSelector(profileContext.selectors.readingsRangeDate(0));
    const readings60 = useSelector(profileContext.selectors.readingsRangeDate(30));
    const readings90 = useSelector(profileContext.selectors.readingsRangeDate(60));
    const isInProgress = useSelector(CloudDrive.selectors.isFetchReadingsInProgress);
    const fetchReadings = useAction(CloudDrive.actions.fetchReadings);
    const conversion = useConversion('bloodGlucoseConcentration');
    const standards = useStandards();
    const { chartData, chartDataPreMeal, chartDataPostMeal } = useChartData(readings30, customRanges, batchesIndex);
    const ticks = useTicks(customRanges);
    const { phiSetReferenceKey, accessToken, storageProvider } = medicalRecord;
    const endDate30 = moment.utc().endOf('day');
    const startDate30 = endDate30.clone().add(-29, 'days').startOf('day');
    const endDate60 = startDate30.clone().add(-1, 'days').endOf('day');
    const startDate60 = endDate60.clone().add(-29, 'days').startOf('day');
    const endDate90 = startDate60.clone().add(-1, 'days').endOf('day');
    const startDate90 = endDate90.clone().add(-29, 'days').startOf('day');
    useEffect(()=>{
        fetchReadings({
            phiSet,
            phiSetReferenceKey,
            phiSetDocumentId,
            batchesIndex,
            measurementsBatchesIndex,
            accessToken,
            storageProvider,
            startDate: startDate90,
            endDate: endDate30,
            successAction: profileContext.actions.setReadings
        });
    }, []);
    if (!chartData || isInProgress) return null;
    const start = moment.utc().startOf('day').unix();
    const end = moment.utc().endOf('day').unix();
    const chartProps = {
        start,
        end,
        ticks,
        isReadOnly: true,
        isCustomRanges: true,
        isTooltipDisabled: true,
        gridTheme,
        criticalHigh: conversion.toDisplay(standards.critical.highThreshold),
        criticalLow: conversion.toDisplay(standards.preMeal.lowThreshold),
        formatLabel
    };
    const renderUnit = ()=>/*#__PURE__*/ _jsx("p", {
            className: styles.bloodGlucoseConcentrationUnit,
            children: /*#__PURE__*/ _jsx(FormattedMessage, _object_spread_props(_object_spread({}, messages.labels.unit), {
                values: {
                    unit: conversion.unitSymbol
                }
            }))
        });
    return /*#__PURE__*/ _jsxs("div", {
        className: styles.printableBloodGlucoseConcentrationReport,
        children: [
            /*#__PURE__*/ _jsx(Header, {}),
            /*#__PURE__*/ _jsxs("div", {
                className: styles.headerGlucoseConcentrationReport__row,
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                className: styles.chart__title,
                                children: /*#__PURE__*/ _jsx(FormattedMessage, _object_spread({}, messages.glucoseReport.headerGlucoseLevelHeader))
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: styles.glucoseLevel__content,
                                children: [
                                    /*#__PURE__*/ _jsx(GlucoseLevelDistributionChart, {
                                        disableTooltip: true,
                                        conversion: conversion,
                                        standards: standards,
                                        readings: readings30,
                                        start: startDate30,
                                        end: endDate30,
                                        valueKey: "value"
                                    }),
                                    /*#__PURE__*/ _jsx(GlucoseLevelDistributionChart, {
                                        disableTooltip: true,
                                        conversion: conversion,
                                        standards: standards,
                                        readings: readings60,
                                        start: startDate60,
                                        end: endDate60,
                                        valueKey: "value"
                                    }),
                                    /*#__PURE__*/ _jsx(GlucoseLevelDistributionChart, {
                                        disableTooltip: true,
                                        conversion: conversion,
                                        standards: standards,
                                        readings: readings90,
                                        start: startDate90,
                                        end: endDate90,
                                        valueKey: "value"
                                    })
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                className: styles.chart__title,
                                children: [
                                    /*#__PURE__*/ _jsx(FormattedMessage, _object_spread({}, messages.glucoseReport.headerBloodGlucoseHeader)),
                                    /*#__PURE__*/ _jsx("br", {}),
                                    `${formatDate(startDate30)} - ${formatDate(endDate30)}`,
                                    renderUnit()
                                ]
                            }),
                            /*#__PURE__*/ _jsx(GlucoseConcentrationTrend, _object_spread({}, chartData, chartProps)),
                            /*#__PURE__*/ _jsx("div", {
                                className: styles.chart__footer,
                                children: /*#__PURE__*/ _jsx(FormattedMessage, _object_spread({}, messages.glucoseReport.bloodGlucoseBottom))
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(Table, {
                readings30: readings30,
                readings60: readings60,
                readings90: readings90,
                conversion: conversion
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: styles.headerGlucoseConcentrationReport__row,
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                className: styles.chart__title,
                                children: [
                                    /*#__PURE__*/ _jsx(FormattedMessage, _object_spread({}, messages.glucoseReport.headerBloodGlucoseHeaderPreMeal)),
                                    /*#__PURE__*/ _jsx("br", {}),
                                    `${formatDate(startDate30)} - ${formatDate(endDate30)}`,
                                    renderUnit()
                                ]
                            }),
                            /*#__PURE__*/ _jsx(GlucoseConcentrationTrend, _object_spread({}, chartDataPreMeal, chartProps)),
                            /*#__PURE__*/ _jsx("div", {
                                className: cn(styles.chart__footer, styles.greenFont),
                                children: `${intl.formatMessage(messages.glucoseReport.bloodGlucoseBottomPreMeal)}:
               ${conversion.toDisplay(standards.preMeal.lowThreshold)} - 
               ${conversion.toDisplay(standards.preMeal.highThreshold)} ${conversion.unitSymbol}`
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                className: styles.chart__title,
                                children: [
                                    /*#__PURE__*/ _jsx(FormattedMessage, _object_spread({}, messages.glucoseReport.headerBloodGlucoseHeaderPostMeal)),
                                    /*#__PURE__*/ _jsx("br", {}),
                                    `${formatDate(startDate30)} - ${formatDate(endDate30)}`,
                                    renderUnit()
                                ]
                            }),
                            /*#__PURE__*/ _jsx(GlucoseConcentrationTrend, _object_spread({}, chartDataPostMeal, chartProps)),
                            /*#__PURE__*/ _jsx("div", {
                                className: cn(styles.chart__footer, styles.greenFont),
                                children: `${intl.formatMessage(messages.glucoseReport.bloodGlucoseBottomPostMeal)}:
               ${conversion.toDisplay(standards.postMeal.lowThreshold)} - 
               ${conversion.toDisplay(standards.postMeal.highThreshold)} ${conversion.unitSymbol}`
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: styles.textFields__wrapper,
                children: [
                    /*#__PURE__*/ _jsx("h1", {
                        children: /*#__PURE__*/ _jsx(FormattedMessage, _object_spread({}, messages.glucoseReport.textField))
                    }),
                    /*#__PURE__*/ _jsx("hr", {}),
                    /*#__PURE__*/ _jsx("hr", {}),
                    /*#__PURE__*/ _jsx("hr", {})
                ]
            })
        ]
    });
};
export default BloodGlucoseConcentrationReport;
